import pako from 'pako';
import { ApolloClient, ApolloProvider, ApolloLink, InMemoryCache, split } from '@apollo/client';
import { RetryLink } from "@apollo/client/link/retry";
import { notification, message, Alert, ConfigProvider } from 'antd';
import 'antd/dist/reset.css';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createUploadLink } from 'apollo-upload-client';
import React, { useEffect, useState, useMemo, Suspense } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { useRenewLoginTokenMutation } from '../../graphql/User/UserMutations';
import LoginForm from '../User/LoginForm';
import DashboardAuthenticatedRootPage from './DashboardAuthenticatedRootPage';
import User, { AuthPayload, UserInterface } from 'graphql/User/User';
import { USER_QUERY } from 'graphql/User/UserQueries';
import { UserQuery, UserQueryVariables } from 'graphql/User/apollo-graphql-generated/UserQuery';
import { getMainDefinition } from 'apollo-utilities';
import { createClient } from 'graphql-ws';
import PagesController, { ChatOnlinePageLazy } from './PagesController';
import ErrorBoundary from 'components/ErrorBoundary';
import NetworkErrorPage from 'components/NetworkErrorPage';
import { FrownOutlined } from '@ant-design/icons';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance'
import ptBR from 'antd/locale/pt_BR'

// import LogRocket from 'logrocket';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { reportToBugsnag } from 'utils/reportToBugsnag';
import Bugsnag from '@bugsnag/js';
import { parseClientUrlParams } from 'pages/Chats/ChatOnlinePage/util/util';
import { LoginExternalChatOnlineMutation_loginExternalChatOnline } from 'graphql/User/apollo-graphql-generated/LoginExternalChatOnlineMutation';
import useChatOnlineStore from 'pages/Chats/ChatOnlinePage/pages/NewChatOnlinePage/store/useChatOnlineStore';
import PageLoadingIndicator from 'components/PageLoadingIndicator';
// set global dayjs locale to portuguese
dayjs.locale('pt-br');

if (process.env.NODE_ENV === 'production') {
  // LogRocket.init('pyvtlx/firefly');
}

const TOKEN_RENEW_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Component that renews the user token every hour */
export const UserTokenRenewer = (props: { token: string; onSuccessfulTokenRenew: (authPayload: Pick<AuthPayload, 'token' | 'user'>) => void }) => {
  const { token, onSuccessfulTokenRenew } = props;
  // renew token automatically
  const [renewTokenMutation] = useRenewLoginTokenMutation({
    variables: { token },
    update: (proxy, mutationResult) => {
      if (mutationResult.data.renewLoginToken) {
        // console.log(`Login Token renewed`);
        onSuccessfulTokenRenew(mutationResult.data.renewLoginToken);
      } else {
        // console.log("Login token not renewed")
      }
    },
  });

  //Renews token on component mount if more than 24 hours passed since last renew
  useEffect(() => {
    const lastRenewDateStr = localStorage.getItem(USER_TOKEN_RENEW_DATE_LOCAL_STORAGE_KEY);
    const lastRenewTime = lastRenewDateStr ? new Date(lastRenewDateStr).getTime() : 0;
    const timeSinceLastRenew = new Date().getTime() - lastRenewTime;

    if (token && timeSinceLastRenew > TOKEN_RENEW_INTERVAL_MS) renewTokenMutation();
    // eslint-disable-next-line
  }, []);

  //Renews token every 24 hours
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (token) renewTokenMutation();
    }, TOKEN_RENEW_INTERVAL_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line
  }, []);

  return <></>;
};

const USER_LOCAL_STORAGE_KEY = 'firefly-dashboard-user';
const USER_TOKEN_LOCAL_STORAGE_KEY = 'firefly-dashboard-user-token';
const USER_TOKEN_RENEW_DATE_LOCAL_STORAGE_KEY = 'firefly-dashboard-user-token-renew-date';
export const SELECTED_WIDGET_IDS_LOCAL_STORAGE_KEY = 'firefly-dashboard-selected-widget-ids';

// Shows Links to different Routes (FireFly_Manager, Salesman, Salesman_Manager) depending on user access levels
const createApolloClient = (args?: { onLogout?: (redirectTo?: string) => void; onSocketConnected?: () => void; onSocketDisconnected?: () => void }) => {
  const { onLogout, onSocketConnected, onSocketDisconnected } = args || {};
  let endpoint = process.env.REACT_APP_API_ENDPOINT;
  let endpointWs = process.env.REACT_APP_API_ENDPOINT_WS;
  const cache = new InMemoryCache();
  // const token = Authentication.getInstance().getToken()

  const getAuthorizationString = () => {
    const token = localStorage.getItem(USER_TOKEN_LOCAL_STORAGE_KEY);
    const authorization = token ? `Bearer ${token}` : null;
    return authorization;
  };

  /** Handles authentication */
  const authLink = setContext((_, { headers }) => {
    // return the headers to the context so httpLink can read them
    const authorization = getAuthorizationString();
    return {
      headers: {
        ...headers,
        authorization,
      },
    };
  });

  /** Handles graphql and connection errors globally */
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    graphQLErrors &&
      graphQLErrors.forEach((error) => {
        if (error.extensions && error.extensions.code === 'TokenInvalidError') {
          //If the token is invalid or expired, go to the login form
          // console.log("Token is invalid. Loging out.")
          // notification.open({
          //   message: "Sessão expirada",
          //   description: "Por favor efetuar login novamente",
          // })

          if (process.env.REACT_APP_WHITELABEL_BUILD)
            return

          if (onLogout) {
            const pathname = window.location.pathname === '/' ? '' : window.location.pathname
            // // redirect to special login page if inside client details page
            // const redirectTo: string = window.location.pathname.startsWith('/client/') ? window.location.href + '/login' : '/';
            const redirectToPath = pathname ? `/?redirectTo=${encodeURIComponent(pathname)}` : ''
            const redirectTo = redirectToPath + window.location.search

            onLogout(redirectTo);
          } else {
            console.log(`[Apollo onError] onLogout() skipped`);
          }
        }
      });

    if (networkError) console.log(`[Network error]: ${networkError}`);
  });

  const httpLink = createUploadLink({
    uri: endpoint,
    headers: {
      // authorization: token ? `Bearer ${token}` : '',
      frontEndVersion: process.env.REACT_APP_VERSION,
    },
  });

  const retryLink = new RetryLink({
    attempts: {
      max: 4,
      retryIf: (error, operation) => {
        if (!error || error.message !== 'Failed to fetch') return false;
        const definition = getMainDefinition(operation.query);
        // retry only queries, not mutations or subscriptions
        return definition?.kind === 'OperationDefinition' && definition?.operation === 'query';
      },
    },
    delay: {
      initial: 1000,
      max: 10000,
      jitter: true,
    },
  });

  const httpLinkWithRetry = ApolloLink.from([retryLink, httpLink])

  // Create a WebSocket link using graphql-ws:
  const wsClient = createClient({
    url: endpointWs,
    connectionParams: () => ({
      // send auth token when the connection is started
      authorization: getAuthorizationString(),
    }),
    retryAttempts: Infinity, // Retry indefinitely
    shouldRetry: () => true, // Always retry on disconnect
    retryWait: (retries) => {
      // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
      const delay = Math.min(1000 * Math.pow(2, retries), 30000);
      return new Promise((resolve) => setTimeout(resolve, delay));
    },
    on: {
      connected: () => onSocketConnected && onSocketConnected(),
      closed: () => onSocketDisconnected && onSocketDisconnected(),
    },
  });

  const wsLink = new GraphQLWsLink(wsClient);

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  const httpAndWebSocketsLink = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLinkWithRetry,
  );

  /** Merge the links */
  const link = ApolloLink.from([errorLink, authLink, httpAndWebSocketsLink]);

  const client = new ApolloClient({ link, cache, connectToDevTools: process.env.NODE_ENV !== 'production' }); // create Apollo client
  return client;
};

const compressUserToStorage = (user: User): string => {
  try {
    const json = JSON.stringify(user);
    const compressed = pako.deflate(json);
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < compressed.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, Array.from(compressed.subarray(i, i + chunkSize)));
    }
    return btoa(binary);
  } catch (e) {
    console.log('[compressUserToStorage] Error compressing user for local storage.', e);
    return JSON.stringify(user); // Fall back to uncompressed (legacy format)
  }
};

const decompressUserFromStorage = (data: string): UserInterface => {
  try {
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return JSON.parse(pako.inflate(bytes, { to: 'string' }));
  } catch (e) {
    console.log('[decompressUserFromStorage] Error decompressing user from local storage.', e);
    // Fall back to uncompressed (legacy format)
    return JSON.parse(data);
  }
};

const getUserFromLocalStorage = (): User => {
  try {
    const raw = localStorage.getItem(USER_LOCAL_STORAGE_KEY);
    if (!raw) return null;
    const userInterface: UserInterface = decompressUserFromStorage(raw);
    return new User(userInterface);
  } catch (e) {
    console.log('[getUserFromLocalStorage] Error parsing user from local storage.', e);
    return null;
  }
};

if (process.env.REACT_APP_BUGSNAG_API_TOKEN && process.env.NODE_ENV === 'production') {
  const user = getUserFromLocalStorage();
  Bugsnag.start({
    apiKey: process.env.REACT_APP_BUGSNAG_API_TOKEN,
    plugins: [new BugsnagPluginReact()],
    user: {
      email: user?.email,
      id: user?.id,
      name: user?.name,
    },
    appVersion: process.env.REACT_APP_VERSION,
  });
  BugsnagPerformance.start({ apiKey: process.env.REACT_APP_BUGSNAG_API_TOKEN })
}

const DashboardRoot = (props: {}) => {
  const userInitial = useMemo(() => getUserFromLocalStorage(), []);

  useEffect(() => {
    setTimeout(() => {
      const user = getUserFromLocalStorage()
      if (!user) {
        console.log('Clearing local storage.');
        localStorage.removeItem(SELECTED_WIDGET_IDS_LOCAL_STORAGE_KEY);
        localStorage.removeItem(USER_TOKEN_RENEW_DATE_LOCAL_STORAGE_KEY);
        localStorage.removeItem(USER_TOKEN_LOCAL_STORAGE_KEY);
        localStorage.removeItem(USER_LOCAL_STORAGE_KEY);
      }
    }, 1000)
  }, []);

  //Gets token from local storage
  const [token, setToken] = useState<string>(localStorage.getItem(USER_TOKEN_LOCAL_STORAGE_KEY));
  const [user, setUser] = useState<User>(userInitial);
  const [authenticated, setAuthenticated] = useState<boolean>(!!token);
  const history = useHistory();
  const { setCaches: setChatOnlineCaches } = useChatOnlineStore();

  useEffect(() => {
    // preload chat online to speed up the loading time
    if (process.env.REACT_APP_WHITELABEL_BUILD) {
      ChatOnlinePageLazy?.preload()
    }
  }, [])


  const options = parseClientUrlParams(new URLSearchParams(window.location.search));

  useEffect(() => {
    if (!token) {
      //log out case
      localStorage.removeItem(SELECTED_WIDGET_IDS_LOCAL_STORAGE_KEY);
      localStorage.removeItem(USER_TOKEN_RENEW_DATE_LOCAL_STORAGE_KEY);
      localStorage.removeItem(USER_TOKEN_LOCAL_STORAGE_KEY);
      localStorage.removeItem(USER_LOCAL_STORAGE_KEY);
    } else {
      // log in case
      localStorage.setItem(USER_TOKEN_LOCAL_STORAGE_KEY, token);
      localStorage.setItem(USER_LOCAL_STORAGE_KEY, compressUserToStorage(user));
    }
    Bugsnag.setUser(user?.id, user?.email, user?.name);
    setAuthenticated(!!token && !!user);
  }, [token, user]);

  const handleLogout = async (redirectTo?: string) => {
    // console.log('handleLogout', authenticated)
    // if (authenticated) {
    if (!process.env.REACT_APP_WHITELABEL_BUILD)
      notification.open({
        message: 'Desconectado',
        description: 'Por favor, efetue o login novamente para continuar',
      });

    // avoids a bug where the redirect url is a JS code function
    const redirectTo2 = (typeof redirectTo !== 'string' || redirectTo?.startsWith('function')) ?
      null : redirectTo;

    setTimeout(async () => {
      await apolloClient.clearStore();
      setUser(null);
      setToken(null);
      if (redirectTo2)
        window.location.href = redirectTo2;
    }, 1000);
    // }
  };

  const [isSocketConnected, setIsSocketConnected] = useState<boolean>(false);

  const handleSocketConnected = async () => {
    console.debug('handleSocketConnected');
    setIsSocketConnected(true);
  };

  const handleSocketDisconnected = async () => {
    console.debug('handleSocketDisconnected');
    setIsSocketConnected(false);
  };

  const apolloClient = useMemo(
    () =>
      createApolloClient({
        onLogout: handleLogout,
        onSocketConnected: handleSocketConnected,
        onSocketDisconnected: handleSocketDisconnected,

      }),
    // eslint-disable-next-line
    []
  );

  const handleSuccesfulLogin = async (authPayload: Pick<AuthPayload, 'token' | 'user'>, options?: { redirectTo?: string }) => {
    const { redirectTo } = options || {}

    localStorage.setItem(USER_TOKEN_LOCAL_STORAGE_KEY, authPayload.token);
    localStorage.setItem(USER_TOKEN_RENEW_DATE_LOCAL_STORAGE_KEY, new Date().toISOString());
    await apolloClient.clearStore();
    setUser(new User(authPayload.user));
    setToken(authPayload.token);

    // console.log('handleSuccesfulLogin', { redirectTo })
    if (redirectTo)
      history.push(redirectTo)
    // window.location.href = redirectTo;
  };

  const handleSuccessfulTokenRenew = async (authPayload: Pick<AuthPayload, 'token' | 'user'>) => {
    localStorage.setItem(USER_TOKEN_LOCAL_STORAGE_KEY, authPayload.token);
    localStorage.setItem(USER_TOKEN_RENEW_DATE_LOCAL_STORAGE_KEY, new Date().toISOString());
    setUser(new User(authPayload.user));
    setToken(authPayload.token);
  };

  const handleSuccesfulLoginExternalChatOnline = async (loginResult: LoginExternalChatOnlineMutation_loginExternalChatOnline, options?: { redirectTo?: string }) => {
    const { redirectTo } = options || {}

    // save all preloaded vars in local cache
    setChatOnlineCaches({
      messagesCache: loginResult?.messages,
      externalChatChannelsCache: loginResult?.externalChatChannels,
      chatClientsCache: loginResult?.chatClients,
      // TODO: implement seting templates cache (using apollo client write query?)
      templatesCache: loginResult?.templates,
      usersCache: loginResult?.users,
    })

    // if (loginResult?.templates) {
    //   // update apollo client cache
    //   const widgets = loginResult?.authPayload?.user?.agent?.agencies?.map(cur => cur?.chatWidgets).flat() || []
    //   const widgetsFiltered = widgets.filter(w => w.externalChatChannels?.some(ch => loginResult?.externalChatChannels?.some(ch2 => ch2.id === ch.id)))
    //   const widgetIdsFiltered = widgetsFiltered.map(w => w.id)
    //   // console.log({ widgetIdsFiltered, templates: loginResult?.templates })

    //   writeAgencyTemplateByWidgetIdsQuery(apolloClient, {
    //     variables: { widgetIds: widgetIdsFiltered },
    //     data: {
    //       agencyTemplateByWidgetIds: loginResult?.templates || [],
    //     },
    //   })
    // }

    handleSuccesfulLogin(loginResult?.authPayload, { redirectTo })
  }

  /** gets updated user info from the server. Useful for updating the list of agencies the user has access to. */
  const handleRefetchUser = async () => {
    try {
      const queryResult = await apolloClient.query<UserQuery, UserQueryVariables>({
        query: USER_QUERY,
        variables: { id: 'me' },
        fetchPolicy: 'network-only',
      });
      const newUserData = queryResult && queryResult.data && queryResult.data.user;
      if (newUserData) {
        const newUserObj = new User(newUserData);
        setUser(newUserObj);
        message.info('Lista de agências atualizada');
      } else {
        notification.error({ message: 'Erro', description: 'Não foi possível receber dados atualizados do seu usuário. ' + (queryResult.errors || '') });
      }
    } catch (e: any) {
      reportToBugsnag({ error: e, user, caller: 'handleRefetchUser' });
      notification.error({ message: 'Erro', description: 'Não foi possível receber dados atualizados do seu usuário. ' + (e || '') });
    }
  };

  const getExternalLoginToken = (): {
    token: string,
    phone?: string,
    name?: string,
    message?: string,
    businessType?: string,
    redirectTo?: string,
    showMessagesOnly?: string
  } => {
    const pathname = window.location.pathname; // could be '/'
    if (pathname !== '/')
      return null

    const search = window.location.search; // could be '?foo=bar'
    const params = new URLSearchParams(search)

    return {
      token: params.get('token'),
      phone: params.get('phone'),
      name: params.get('name'),
      message: params.get('text'),
      businessType: params.get('businessType'),
      showMessagesOnly: params.get('showMessagesOnly'),
      // redirectTo: '/chatOnline/externalChatChannel/'
    }
  }

  const {
    token: externalLoginToken,
    phone: externalLoginClientPhone,
    name: externalLoginClientName,
    message: externalLoginClientMessage,
    businessType: externalLoginClientBusinessType,
    showMessagesOnly: externalLoginShowMessagesOnly,
  } = getExternalLoginToken() || {}
  // console.log({ externalLoginToken })

  // logout if external token detected - will login again automatically through LoginForm
  useEffect(() => {
    if (externalLoginToken && authenticated)
      handleLogout()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loginForm = <LoginForm
    onSuccessfulLogin={handleSuccesfulLogin}
    onSuccessfulLoginExternalChatOnline={handleSuccesfulLoginExternalChatOnline}
    isRefreshingUserToken={token && !user}
    externalLoginToken={externalLoginToken}
    externalLoginClientPhone={externalLoginClientPhone}
    externalLoginClientName={externalLoginClientName}
    externalLoginClientMessage={externalLoginClientMessage}
    externalLoginClientBusinessType={externalLoginClientBusinessType}
    externalLoginShowMessagesOnly={externalLoginShowMessagesOnly}
    clientSecretToShare={options.secret}
    clientIdToShare={options.id}
  />

  const openRoutes = [
    ...PagesController.getReactRouterRoutes({ openRoutesOnly: true, whitelabelRoutesOnly: !!process.env.REACT_APP_WHITELABEL_BUILD }),

    <Route key="login" path="/" exact render={() => loginForm} />,
    // if no other routes match, redirect to the login page
    <Route key="login_2" render={() => loginForm} />,
  ];

  return (
    <ApolloProvider client={apolloClient}>
      <ConfigProvider locale={ptBR}>
        <UserTokenRenewer token={token} onSuccessfulTokenRenew={handleSuccessfulTokenRenew} />
        <ErrorBoundary
          errorFallback={
            <Alert
              icon={<FrownOutlined />}
              showIcon
              type="error"
              message="Erro"
              description="Ocorreu um erro inesperado. Verifique o console do navegador para mais informações."
            />
          }
          networkErrorFallback={<NetworkErrorPage />}
        >
          <Suspense
            fallback={
              <PageLoadingIndicator
                debug={'DashboardRootPage Suspense'}
                tip={!process.env.REACT_APP_WHITELABEL_BUILD ? 'Carregando página...' : null}
              />}
          >
            {authenticated ? (
              // Shows dashboard
              <DashboardAuthenticatedRootPage onLogout={handleLogout} user={user} onRefetchAgencies={handleRefetchUser} isSocketConnected={isSocketConnected} />
            ) : (
              // Shows Login form (or open routes)
              <Switch>{openRoutes}</Switch>
            )}
          </Suspense>
        </ErrorBoundary>
      </ConfigProvider>
    </ApolloProvider>
  );
};

export default DashboardRoot;
